export const SESSION_MODES = ["SHORT", "NORMAL", "CHALLENGE"] as const;
export type SessionMode = (typeof SESSION_MODES)[number];

export const SESSION_BUCKETS = ["CORE", "MAINTENANCE", "CHALLENGE"] as const;
export type SessionBucket = (typeof SESSION_BUCKETS)[number];

export type SessionPhase =
  | "IDLE"
  | "CORE_ACTIVE"
  | "CORE_COMPLETE"
  | "REVEAL_PENDING"
  | "BONUS_ACTIVE"
  | "COMPLETE"
  | "EARLY_EXIT";

export interface SessionModeConfig {
  readonly targetDurationMs: number;
  readonly minimumCoreItems: number;
  readonly maximumCoreItems: number;
  readonly bucketWeights: Readonly<Record<SessionBucket, number>>;
  readonly maximumNewStrategyIntroductions: 1;
}

export const SESSION_MODE_CONFIG: Readonly<Record<SessionMode, SessionModeConfig>> = {
  SHORT: {
    targetDurationMs: 3 * 60 * 1000,
    minimumCoreItems: 6,
    maximumCoreItems: 10,
    bucketWeights: { CORE: 0.65, MAINTENANCE: 0.25, CHALLENGE: 0.10 },
    maximumNewStrategyIntroductions: 1,
  },
  NORMAL: {
    targetDurationMs: 5 * 60 * 1000,
    minimumCoreItems: 10,
    maximumCoreItems: 16,
    bucketWeights: { CORE: 0.60, MAINTENANCE: 0.25, CHALLENGE: 0.15 },
    maximumNewStrategyIntroductions: 1,
  },
  CHALLENGE: {
    targetDurationMs: 6 * 60 * 1000,
    minimumCoreItems: 10,
    maximumCoreItems: 18,
    bucketWeights: { CORE: 0.55, MAINTENANCE: 0.20, CHALLENGE: 0.25 },
    maximumNewStrategyIntroductions: 1,
  },
};

export interface SessionRuntime {
  readonly id: string;
  readonly phase: SessionPhase;
  readonly mode: SessionMode | null;
  readonly startedAt: string | null;
  readonly endedAt: string | null;
  readonly coreItemCount: number;
  readonly bonusItemCount: number;
  readonly bucketCounts: Readonly<Record<SessionBucket, number>>;
  readonly consecutiveDifficultOutcomes: number;
  readonly strategyIntroductionCount: number;
  readonly bonusRoundOffered: boolean;
  readonly bonusRoundAccepted: boolean;
  readonly achievementUnlockIds: readonly string[];
}

export type SessionEvent =
  | { readonly type: "START"; readonly mode: SessionMode; readonly at: string }
  | {
    readonly type: "RECORD_ITEM";
    readonly bucket: SessionBucket;
    readonly difficultOutcome: boolean;
    readonly introducedStrategy: boolean;
  }
  | { readonly type: "COMPLETE_CORE" }
  | { readonly type: "QUEUE_REVEALS"; readonly achievementUnlockIds: readonly string[] }
  | { readonly type: "FINISH_REVEALS" }
  | { readonly type: "START_BONUS" }
  | { readonly type: "STOP"; readonly at: string };

export function createSessionRuntime(id: string): SessionRuntime {
  return {
    id,
    phase: "IDLE",
    mode: null,
    startedAt: null,
    endedAt: null,
    coreItemCount: 0,
    bonusItemCount: 0,
    bucketCounts: { CORE: 0, MAINTENANCE: 0, CHALLENGE: 0 },
    consecutiveDifficultOutcomes: 0,
    strategyIntroductionCount: 0,
    bonusRoundOffered: false,
    bonusRoundAccepted: false,
    achievementUnlockIds: [],
  };
}

function invalidTransition(state: SessionRuntime, event: SessionEvent): never {
  throw new Error(`Ongeldige sessietransitie: ${state.phase} + ${event.type}.`);
}

export function transitionSession(
  state: SessionRuntime,
  event: SessionEvent,
): SessionRuntime {
  if (event.type === "START") {
    if (state.phase !== "IDLE") return invalidTransition(state, event);
    return { ...state, phase: "CORE_ACTIVE", mode: event.mode, startedAt: event.at };
  }

  if (event.type === "RECORD_ITEM") {
    if (state.phase !== "CORE_ACTIVE" && state.phase !== "BONUS_ACTIVE") {
      return invalidTransition(state, event);
    }
    const isCore = state.phase === "CORE_ACTIVE";
    return {
      ...state,
      coreItemCount: state.coreItemCount + (isCore ? 1 : 0),
      bonusItemCount: state.bonusItemCount + (isCore ? 0 : 1),
      bucketCounts: {
        ...state.bucketCounts,
        [event.bucket]: state.bucketCounts[event.bucket] + 1,
      },
      consecutiveDifficultOutcomes: event.difficultOutcome
        ? state.consecutiveDifficultOutcomes + 1
        : 0,
      strategyIntroductionCount: state.strategyIntroductionCount
        + (event.introducedStrategy ? 1 : 0),
    };
  }

  if (event.type === "COMPLETE_CORE") {
    if (state.phase !== "CORE_ACTIVE") return invalidTransition(state, event);
    return { ...state, phase: "CORE_COMPLETE", bonusRoundOffered: true };
  }

  if (event.type === "QUEUE_REVEALS") {
    if (state.phase !== "CORE_COMPLETE") return invalidTransition(state, event);
    if (event.achievementUnlockIds.length === 0) return state;
    return {
      ...state,
      phase: "REVEAL_PENDING",
      achievementUnlockIds: [
        ...new Set([...state.achievementUnlockIds, ...event.achievementUnlockIds]),
      ],
    };
  }

  if (event.type === "FINISH_REVEALS") {
    if (state.phase !== "REVEAL_PENDING") return invalidTransition(state, event);
    return { ...state, phase: "CORE_COMPLETE" };
  }

  if (event.type === "START_BONUS") {
    if (state.phase !== "CORE_COMPLETE") return invalidTransition(state, event);
    return { ...state, phase: "BONUS_ACTIVE", bonusRoundAccepted: true };
  }

  if (event.type === "STOP") {
    if (state.phase === "IDLE" || state.phase === "COMPLETE" || state.phase === "EARLY_EXIT") {
      return invalidTransition(state, event);
    }
    return {
      ...state,
      phase: state.phase === "CORE_ACTIVE" ? "EARLY_EXIT" : "COMPLETE",
      endedAt: event.at,
    };
  }

  return invalidTransition(state, event);
}

export function shouldCompleteCore(
  state: SessionRuntime,
  now: string,
): boolean {
  if (state.phase !== "CORE_ACTIVE" || !state.mode || !state.startedAt) return false;
  const config = SESSION_MODE_CONFIG[state.mode];
  if (state.coreItemCount >= config.maximumCoreItems) return true;
  const elapsedMs = Date.parse(now) - Date.parse(state.startedAt);
  return elapsedMs >= config.targetDurationMs
    && state.coreItemCount >= config.minimumCoreItems;
}

export function chooseSessionBucket(state: SessionRuntime): SessionBucket {
  if (!state.mode) throw new Error("Een sessiemodus is nodig om een bucket te kiezen.");
  const weights = SESSION_MODE_CONFIG[state.mode].bucketWeights;
  const nextTotal = Object.values(state.bucketCounts).reduce((sum, value) => sum + value, 0) + 1;

  return SESSION_BUCKETS
    .map((bucket) => ({
      bucket,
      deficit: weights[bucket] * nextTotal - state.bucketCounts[bucket],
    }))
    .sort((a, b) => b.deficit - a.deficit || SESSION_BUCKETS.indexOf(a.bucket) - SESSION_BUCKETS.indexOf(b.bucket))[0]
    .bucket;
}
